import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Ask a Small Group or Sunday School Class to Support You as a Missionary';
const DESCRIPTION =
    "A small group or Sunday school class isn't a mini-church service and isn't a one-on-one coffee either. A practical guide to getting on the schedule, structuring a short ask, and following up with a group instead of an individual.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover23.jpg',
            datePublished: '2026-08-08',
            dateModified: '2026-08-08',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-ask-a-small-group-or-sunday-school-class-to-support-you',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How much time should I ask for with a small group or class?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Five to ten minutes, and say so when you ask for the slot. A group leader can almost always find ten minutes; a request to 'share for a while' feels open-ended and is easier to put off or shorten on the spot.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I ask the whole group to give, or individuals privately?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Do both. Make one clear ask to the room so everyone hears the same invitation and amount, then follow up individually afterward — a group setting is where people hear about you, not usually where they commit on the spot.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is a sign-up sheet or a QR code better for a small group ask?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Use both. A paper sign-up sheet captures names and emails from people who aren't ready to give in the room, and a QR code to your giving page lets anyone who is ready act immediately, on their phone, before the moment passes.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Do I need permission to ask a Sunday school class for financial support?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — check with the class teacher or small-group leader first, and loop in church staff if the class meets on church property or during church hours. Most churches are glad to have a missionary they know share, but they want to know it's happening ahead of time.",
                    },
                },
            ],
        },
    ],
};

export default function HowToAskASmallGroupOrSundaySchoolClassToSupportYou() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-ask-a-small-group-or-sunday-school-class-to-support-you' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-small-group-ask-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover23.jpg" alt="A missionary speaking to a small group seated in a circle in a church classroom" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most support-raising advice covers two settings: a whole-church Sunday service, or a one-on-one coffee with a potential donor. A small group or Sunday school class sits in between — smaller and more personal than the sanctuary, but more public and time-boxed than a coffee. It deserves its own approach, not a shrunk-down version of either.</p>

                    <h2>Why a small group or class is worth a dedicated ask</h2>

                    <p>People in an established small group or class already trust each other and are used to sharing prayer requests and life updates — that atmosphere lowers the guard that a formal pulpit announcement can raise. It's also a room where you can actually answer questions in real time, which a two-minute platform announcement never allows. And because it's a recurring group, a well-received visit often turns into a standing invitation to come back with updates, which a one-time church-wide appeal rarely does.</p>

                    <h2>Getting on the schedule</h2>

                    <p>Go to the group leader or Sunday school teacher directly, not the church office, unless the class meets on church property during church hours — in that case, loop in staff too. Ask for a specific amount of time (five to ten minutes) inside their existing meeting, not a special session built around you; groups protect their regular format, and asking to fit inside it rather than replace it makes yes easy. Give the leader a one-line description of what you'll cover so they can introduce you accurately.</p>

                    <h2>Structuring the ask itself</h2>

                    <p>Keep it to three parts: who you are and what you're doing (60-90 seconds — see <a href="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations">how to write a fundraising bio that gets donations</a> for the same discipline applied to a page bio), one specific need or number (a dollar amount, a percentage of your goal remaining, or a number of monthly partners you still need), and one clear next step — not three options. "I'd love for five people in this room to consider $30/month" is a request people can actually act on; "please pray about how you might support us" usually produces prayer and nothing else.</p>

                    <h2>Group giving vs. individual follow-up</h2>

                    <p>Make the ask to the whole room so everyone hears the same invitation, but don't expect commitments on the spot — most people need to check a budget or talk to a spouse first. Leave a paper sign-up sheet for names and emails, and put a QR code to your giving page on a handout or slide for anyone ready to act immediately. Then follow up individually within a week with anyone who signed up, by name, referencing the specific thing you asked for — see <a href="/resources/how-to-ask-for-support-over-text-and-social-media">how to ask for support over text and social media</a> for how to keep that follow-up personal instead of a mass blast.</p>

                    <h2>Common mistakes</h2>

                    <p>Showing up without a specific ask and hoping the group "figures out how to help" wastes a rare captive audience. So does treating it like a full sermon and running long — you're a guest in someone else's meeting time. And skipping the follow-up is the biggest one: a sign-up sheet that never gets a follow-up email is worse than not collecting names at all, because it teaches people their interest didn't matter.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How much time should I ask for?</strong><br />
                    Five to ten minutes, stated up front when you request the slot.</p>

                    <p><strong>Should I ask the group or individuals?</strong><br />
                    Both — one clear ask to the room, then individual follow-up afterward.</p>

                    <p><strong>Sign-up sheet or QR code?</strong><br />
                    Both — the sheet catches people who need to think it over, the QR code catches people ready to act now.</p>

                    <p><strong>Do I need permission first?</strong><br />
                    Yes — check with the group leader or teacher, and church staff if it's on church property or during church hours.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-ask-your-church-to-support-you-as-a-missionary">How to Ask Your Church to Support You as a Missionary</a></li>
                        <li><a href="/resources/how-to-ask-friends-and-family-to-support-your-mission-trip">How to Ask Friends and Family to Support Your Mission Trip</a></li>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built a direct, recurring-first giving page so that a QR code shown in a small group or a link shared after class can turn into a monthly supporter in under a minute.</p>
                    <p><small><em>Last updated: 2026-08-08.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-ask-a-small-group-or-sunday-school-class-to-support-you" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
